// Utility per gestire tooltip su dispositivi mobile con supporto touch
class MobileTooltipManager {
  constructor() {
    this.activeTooltips = new Set();
    this.timeouts = new Map();
    this.touchStartPosition = { x: 0, y: 0 };
    this.moveThreshold = 10; // Soglia di movimento in pixel
    this.isScrolling = false; // Flag per tracciare lo stato di scroll
    this.scrollTimeout = null;
    // Memorizza i riferimenti ai listener per la rimozione corretta
    this.boundHandlers = {
      touchStart: this.handleTouchStart.bind(this),
      touchEnd: this.handleTouchEnd.bind(this),
      touchMove: this.handleTouchMove.bind(this),
      scroll: this.handleScroll.bind(this)
    };
    this.init();
  }

  init() {
    // Aggiungi listener globali per gestire tooltip mobile
    if (this.isTouchDevice()) {
      // Usa listener passivi per evitare interferenze con lo scroll
      document.addEventListener('touchstart', this.boundHandlers.touchStart, { passive: true });
      document.addEventListener('touchend', this.boundHandlers.touchEnd, { passive: true });
      document.addEventListener('touchmove', this.boundHandlers.touchMove, { passive: true });
      document.addEventListener('scroll', this.boundHandlers.scroll, { passive: true });
      // Aggiungi listener per resize della finestra
      window.addEventListener('resize', this.boundHandlers.scroll, { passive: true });
    }
  }

  isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  handleTouchStart(event) {
    // Memorizza la posizione iniziale del tocco
    if (event.touches && event.touches[0]) {
      this.touchStartPosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
    }

    const target = event.target.closest('[data-tooltip]');
    
    if (target) {
      // Solo gestisci il tooltip, non interferire con lo scroll
      const tooltipText = target.getAttribute('data-tooltip');
      
      if (tooltipText && !this.activeTooltips.has(target)) {
        this.showTooltip(target, tooltipText);
      } else if (this.activeTooltips.has(target)) {
        this.hideTooltip(target);
      }
      // Non bloccare la propagazione per permettere lo scroll
    } else {
      // Nasconde tutti i tooltip se tocchi altrove, ma solo se non stai scrollando
      // Aggiungi un piccolo delay per distinguere tra tap e inizio scroll
      setTimeout(() => {
        if (!this.isScrolling) {
          this.hideAllTooltips();
        }
      }, 50);
    }
  }

  handleTouchEnd(event) {
    // Reset del flag di scrolling dopo il touchend
    setTimeout(() => {
      this.isScrolling = false;
    }, 100);
  }

  handleTouchMove(event) {
    // Calcola la distanza di movimento solo se ci sono tooltip attivi
    if (this.activeTooltips.size > 0 && event.touches && event.touches[0]) {
      const currentX = event.touches[0].clientX;
      const currentY = event.touches[0].clientY;
      
      const deltaX = Math.abs(currentX - this.touchStartPosition.x);
      const deltaY = Math.abs(currentY - this.touchStartPosition.y);
      
      // Se il movimento supera la soglia, imposta il flag di scrolling e nascondi i tooltip
      if (deltaX > this.moveThreshold || deltaY > this.moveThreshold) {
        this.isScrolling = true;
        this.hideAllTooltips();
      }
    }
  }

  handleScroll() {
    this.isScrolling = true;
    this.hideAllTooltips();
    
    // Reset del flag di scrolling dopo un breve delay
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = setTimeout(() => {
      this.isScrolling = false;
    }, 150);
  }

  showTooltip(element, text) {
    // Rimuovi eventuali tooltip esistenti su questo elemento
    this.hideTooltip(element);

    // Crea il tooltip element
    const tooltip = document.createElement('div');
    tooltip.className = 'mobile-tooltip';
    tooltip.textContent = text;
    
    // Posiziona il tooltip
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    
    // Imposta stili (identici ai tooltip React)
    Object.assign(tooltip.style, {
      position: 'fixed',
      backgroundColor: '#374151',
      color: 'white',
      padding: '12px 16px',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: '500',
      zIndex: '10000',
      maxWidth: '280px',
      whiteSpace: 'normal',
      pointerEvents: 'none',
      opacity: '0',
      transition: 'opacity 0.2s ease',
      boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
      textAlign: 'center',
      lineHeight: '1.4',
      minWidth: '200px',
      wordBreak: 'break-word'
    });

    // Aggiungi al DOM per calcolare dimensioni
    document.body.appendChild(tooltip);
    const tooltipRect = tooltip.getBoundingClientRect();

    // Calcola posizione ottimale
    let top = rect.top - tooltipRect.height - 8;
    let left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);

    // Aggiustamenti per rimanere nel viewport
    if (left < 8) left = 8;
    if (left + tooltipRect.width > viewportWidth - 8) {
      left = viewportWidth - tooltipRect.width - 8;
    }
    if (top < 8) {
      top = rect.bottom + 8; // Mostra sotto se non c'è spazio sopra
    }

    // Applica posizione finale
    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;

    // Anima l'entrata
    requestAnimationFrame(() => {
      tooltip.style.opacity = '1';
    });

    // Aggiungi freccia (identica ai tooltip React)
    const arrow = document.createElement('div');
    Object.assign(arrow.style, {
      position: 'absolute',
      width: '0',
      height: '0',
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      left: '50%',
      transform: 'translateX(-50%)'
    });

    if (top < rect.top) {
      // Freccia sotto (tooltip sopra l'elemento)
      arrow.style.top = '100%';
      arrow.style.borderTop = '8px solid #374151';
    } else {
      // Freccia sopra (tooltip sotto l'elemento)
      arrow.style.bottom = '100%';
      arrow.style.borderBottom = '8px solid #374151';
    }

    tooltip.appendChild(arrow);

    // Traccia stato
    element._mobileTooltip = tooltip;
    this.activeTooltips.add(element);

    // Auto-hide dopo 3 secondi
    const timeout = setTimeout(() => {
      this.hideTooltip(element);
    }, 3000);

    this.timeouts.set(element, timeout);
  }

  hideTooltip(element) {
    if (element._mobileTooltip) {
      element._mobileTooltip.style.opacity = '0';
      setTimeout(() => {
        if (element._mobileTooltip && element._mobileTooltip.parentNode) {
          element._mobileTooltip.parentNode.removeChild(element._mobileTooltip);
        }
        delete element._mobileTooltip;
      }, 200);
    }

    this.activeTooltips.delete(element);
    
    if (this.timeouts.has(element)) {
      clearTimeout(this.timeouts.get(element));
      this.timeouts.delete(element);
    }
  }

  hideAllTooltips() {
    this.activeTooltips.forEach(element => {
      this.hideTooltip(element);
    });
  }

  destroy() {
    this.hideAllTooltips();
    
    // Pulisci i timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
    
    // Rimuovi tutti i listener
    document.removeEventListener('touchstart', this.boundHandlers.touchStart);
    document.removeEventListener('touchend', this.boundHandlers.touchEnd);
    document.removeEventListener('touchmove', this.boundHandlers.touchMove);
    document.removeEventListener('scroll', this.boundHandlers.scroll);
    window.removeEventListener('resize', this.boundHandlers.scroll);
  }
}

// Inizializza automaticamente quando il DOM è pronto
let tooltipManager = null;

const initMobileTooltips = () => {
  if (!tooltipManager) {
    tooltipManager = new MobileTooltipManager();
  }
};

// Auto-inizializza quando il documento è pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMobileTooltips);
} else {
  initMobileTooltips();
}

export default MobileTooltipManager;
export { initMobileTooltips }; 